// // services/internship.service.ts

import { axiosInstance } from "@/lib/axiosInstance";
import { AxiosError } from "axios";



export interface InternshipType {
  id: string;
  title: string;
  description: string;
  type: string;
  domain: string;
  location: string;
  is_remote: boolean;
  stipend_min: number;
  stipend_max: number;
  openings: number;
  duration_weeks: number;
  start_date: string;
  application_deadline: string;
  required_skills: string[];
  requires_partnership: boolean;
  target_degree_types: string[];
  createdAt: string;
  updatedAt: string;
  status?: string;
}

export interface InternshipCreateType {
  title: string;
  description: string;
  type: string;
  domain: string;
  location: string;
  is_remote: boolean;
  stipend_min: number;
  stipend_max: number;
  openings: number;
  duration_weeks: number;
  start_date: string;
  application_deadline: string;
  required_skills: string[];
  requires_partnership: boolean;
  target_degree_types: string[];
}

export interface InternshipMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type InternshipParamsType = {
  search?: string;
  status?: string;
  domain?: string | null;
  location?: string | null;
  is_remote?: boolean | null;
  page?: number;
  limit?: number;
};

export interface InternshipApplication {
  id: string;
  student_application_id: string;
  internship_id: string;
  student_id: string;
  college_id: string;
  resume_url: string;
  cover_letter: string | null;
  status: string;
  applied_at: string;
  updated_at: string;
}

// Interviews are read-only for students and live in interview.service.ts.
// Scheduling/updating is a recruiter action with no student-facing API.

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllInternships = async (filters: InternshipParamsType) => {
  const res = await axiosInstance.get("v1/internships", {
    params: filters,
  });

  let data = res.data;
  if (typeof data === "string") {
    try { data = JSON.parse(data); } catch (e) { }
  }

  return {
    internships: Array.isArray(data?.data?.items) ? data.data.items : (Array.isArray(data?.items) ? data.items : (Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []))),
    metaData: data?.data?.meta || data?.meta || { pagination: { total: data?.data?.total || data?.total || 0 } },
  };
};

/**
 * Resolves to `null` when the posting no longer exists on the recruiter side.
 * The recruiter service answers 404 for a deleted internship, but the student
 * service currently wraps that upstream 404 as a 503 ("Recruiter Service
 * unavailable"), so both codes mean "gone". Returning null instead of throwing
 * caches the outcome as a success — without it, every applications-list
 * remount re-fires one doomed lookup per orphaned card.
 */
export const fetchInternshipById = async (id: string) => {
  try {
    const res = await axiosInstance.get(`/v1/internships/${id}`);
    let data = res.data;
    if (typeof data === "string") {
      try { data = JSON.parse(data); } catch { /* not JSON — use as-is */ }
    }
    return data?.data || data;
  } catch (error) {
    const status = (error as AxiosError)?.response?.status;
    if (status === 404 || status === 503) return null;
    throw error;
  }
};

export const fetchInternshipApplications = async (id: string, params?: { page?: number; limit?: number; status?: string }) => {
  const res = await axiosInstance.get(`/v1/internships/${id}/applications`, { params });
  return res.data;
};

export const updateApplicationStatus = async (applicationId: string, status: string) => {
  const res = await axiosInstance.patch(`/v1/applications/${applicationId}/status`, { status });
  return res.data;
};

export const createInternship = async (data: InternshipCreateType) => {
  const res = await axiosInstance.post("/v1/internships", data);
  return res.data;
};

export const updateInternship = async (
  id: string,
  data: InternshipCreateType
) => {
  const res = await axiosInstance.put(`/v1/internships/${id}`, data);
  return res.data;
};

export const publishInternship = async (id: string) => {
  return axiosInstance.patch(`/v1/internships/${id}/publish`);
};

export const closeInternship = async (id: string) => {
  return axiosInstance.patch(`/v1/internships/${id}/close`);
};

export const deleteInternship = async (id: string) => {
  const res = await axiosInstance.delete(`/v1/internships/${id}`);
  return res.data;
};

// ─── Company Discovery Service Functions ────────────────────────────────────────

export interface DiscoveryCompanyType {
  id: string;
  name?: string;
  industry?: string;
  location?: string;
  headquarters?: string;
  description?: string;
  website_url?: string;
  logo_url?: string;
  [key: string]: any;
}

export const fetchAllCompanies = async (_params?: { page?: number; limit?: number }) => {
  // WORKAROUND: GET /v1/internships/companies has a backend FastAPI route ordering bug
  // where it gets incorrectly matched to /v1/internships/{internship_id}.
  // We derive the unique companies list from the working /v1/internships endpoint instead.
  const res = await axiosInstance.get("v1/internships", { params: { page: 1, limit: 100 } });
  let data = res.data;
  if (typeof data === "string") {
    try { data = JSON.parse(data); } catch (e) { }
  }
  const items: any[] = Array.isArray(data?.data?.items) ? data.data.items
    : Array.isArray(data?.items) ? data.items
      : Array.isArray(data?.data) ? data.data
        : Array.isArray(data) ? data : [];

  // Extract unique companies by recruiter_id
  const companyMap = new Map<string, any>();
  items.forEach((item: any) => {
    const rId = item.recruiter_id;
    if (rId && !companyMap.has(rId)) {
      companyMap.set(rId, {
        id: rId,
        name: item.company_name,
        email: item.company_email,
        website: item.company_website,
        address: item.company_address,
        industry: item.company_industry,
        description: item.company_description,
        headquarters: item.company_headquarters,
      });
    }
  });

  const companies = Array.from(companyMap.values());
  return {
    companies,
    metaData: { pagination: { total: companies.length } },
  };
};

export const fetchCompanyProfile = async (recruiterId: string): Promise<DiscoveryCompanyType> => {
  if (!recruiterId || recruiterId === "undefined") {
    throw new Error("Invalid recruiter ID");
  }
  const res = await axiosInstance.get(`v1/internships/companies/${recruiterId}`);
  let data = res.data;
  if (typeof data === "string") {
    try { data = JSON.parse(data); } catch (e) { }
  }
  return data?.data || data;
};

export const fetchCompanyInternships = async (recruiterId: string, params?: { page?: number; limit?: number }) => {
  if (!recruiterId || recruiterId === "undefined") {
    throw new Error("Invalid recruiter ID");
  }

  // Use the working v1/internships endpoint as the company-specific endpoint has a routing bug
  const res = await axiosInstance.get(`v1/internships`, { params: { ...params, limit: 100 } });
  let data = res.data;
  if (typeof data === "string") {
    try { data = JSON.parse(data); } catch (e) { }
  }

  let items = Array.isArray(data?.data?.items) ? data.data.items : (Array.isArray(data?.items) ? data.items : (Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])));

  // WORKAROUND: The backend returns all internships. Apply a strict client-side filter
  // to ensure we only show the internships posted by this specific company.
  const filteredItems = items.filter((item: any) => item.recruiter_id === recruiterId);

  return {
    internships: filteredItems,
    metaData: { pagination: { total: filteredItems.length, page: params?.page || 1, limit: params?.limit || 50 } },
  };
};

// ─── Active Internship ────────────────────────────────────────────────────────

export interface ActiveInternshipOffer {
  id: string;
  application_id: string;
  internship_id: string;
  stipend: number;
  start_date: string;
  end_date: string;
  offer_letter_url: string | null;
  status: string;
  responded_at: string | null;
  expires_at: string;
  internship_title: string | null;
  company_name: string | null;
  created_at: string;
}

export interface ActiveInternshipType {
  has_active_internship: boolean;
  offer: ActiveInternshipOffer | null;
}

/**
 * Token-scoped public route. This replaced internal/v1/students/{id}/active-internship,
 * which took the student id in the path and required no auth at all — so the id
 * could simply be swapped for another student's.
 */
export const fetchActiveInternship = async (): Promise<ActiveInternshipType> => {
  const res = await axiosInstance.get("/v1/me/active-internship");
  let data = res.data;
  if (typeof data === "string") {
    try { data = JSON.parse(data); } catch { /* fall through to the raw value */ }
  }
  return data?.data || data;
};

export interface DashboardResponseType {
  total_applications: number;
  application_by_status: Record<string, number>;
  total_offers: number;
  pending_offers: number;
  accepted_offers: number;
}

export const fetchDashboardAnalytics = async (): Promise<DashboardResponseType> => {
  const res = await axiosInstance.get("/v1/analytics/dashboard");
  return res.data;
};



















